import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StorefrontLayout from "../shared/StorefrontLayout";
import { t } from "../../i18n";

function renderInquiryFieldIcon(type) {
  switch (String(type || "").trim()) {
    case "name":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 21a8 8 0 0 0-16 0"></path>
          <circle cx="12" cy="8" r="4"></circle>
        </svg>
      );
    case "email":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2"></rect>
          <path d="m3 7 9 6 9-6"></path>
        </svg>
      );
    case "whatsapp":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.5 7.5L3 21l2-5.1A8.5 8.5 0 1 1 21 11.5Z"></path>
          <path d="M9.9 10.6c.6 1.2 1.5 2.1 2.7 2.8"></path>
        </svg>
      );
    case "company":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 21h18"></path>
          <path d="M5 21V7l7-4 7 4v14"></path>
          <path d="M9 10h.01"></path>
          <path d="M15 10h.01"></path>
          <path d="M9 14h.01"></path>
          <path d="M15 14h.01"></path>
        </svg>
      );
    case "product":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m3 7 9-4 9 4-9 4-9-4Z"></path>
          <path d="m3 7 9 4 9-4"></path>
          <path d="M12 11v10"></path>
          <path d="m3 7 0 10 9 4 9-4V7"></path>
        </svg>
      );
    case "quantity":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2"></rect>
          <path d="M7 8h10"></path>
          <path d="M7 12h10"></path>
          <path d="M7 16h6"></path>
        </svg>
      );
    case "message":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          <path d="M8 9h8"></path>
          <path d="M8 13h5"></path>
        </svg>
      );
    default:
      return null;
  }
}

export default function ContactPage({ apiBaseUrl, storefrontContacts = {} }) {
  const [currency, setCurrency] = useState("USD");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactWhatsapp, setContactWhatsapp] = useState("");
  const [contactCompany, setContactCompany] = useState("");
  const [inquiryProduct, setInquiryProduct] = useState("");
  const [inquiryQuantity, setInquiryQuantity] = useState("");
  const [referenceFileName, setReferenceFileName] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [searchParams] = useSearchParams();
  const displayContactEmail = String(storefrontContacts?.contactEmail || "").trim();
  const displayContactPhone = String(storefrontContacts?.contactPhone || "").trim();
  const displayContactWhatsapp = String(storefrontContacts?.contactWhatsapp || "").trim();

  async function submit(event) {
    event.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !inquiryProduct.trim() || !message.trim()) {
      return;
    }

    if (!apiBaseUrl) {
      setSent(false);
      setSubmitError(t("inquirySubmitFailed"));
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    setSent(false);

    try {
      const response = await fetch(`${apiBaseUrl}/inquiries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          contactName,
          contactEmail,
          contactWhatsapp,
          contactCompany,
          inquiryProduct,
          inquiryQuantity,
          message,
          referenceFileName,
          sourcePage: typeof window !== "undefined" ? window.location.pathname : "/contact"
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(String(data?.message || "Inquiry submit failed"));
      }

      setSent(true);
      setContactName("");
      setContactEmail("");
      setContactWhatsapp("");
      setContactCompany("");
      setInquiryProduct("");
      setInquiryQuantity("");
      setReferenceFileName("");
      setMessage("");
    } catch (_error) {
      setSent(false);
      setSubmitError(t("inquirySubmitFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  const inquiryPrefill = useMemo(() => {
    const productName = searchParams.get("productName") || "";
    const sku = searchParams.get("sku") || "";
    const model = searchParams.get("model") || "";

    if (!productName && !sku && !model) {
      return null;
    }

    const lines = [
      t("contactInquiryGreeting"),
      productName ? `${t("contactInquiryProductPrefix")} ${productName}` : null,
      sku ? `SKU: ${sku}` : null,
      model ? `${t("contactModelPrefix")}: ${model}` : null,
      t("contactInquiryQuantity"),
      t("contactInquiryDestinationPort")
    ].filter(Boolean);

    return {
      productName,
      sku,
      model,
      message: lines.join("\n")
    };
  }, [searchParams]);

  useEffect(() => {
    if (!inquiryPrefill) {
      return;
    }

    setInquiryProduct((currentValue) => {
      if (currentValue && currentValue.trim().length > 0) {
        return currentValue;
      }

      return inquiryPrefill.productName;
    });

    setMessage((currentMessage) => {
      if (currentMessage && currentMessage.trim().length > 0) {
        return currentMessage;
      }

      return inquiryPrefill.message;
    });
  }, [inquiryPrefill]);

  return (
    <StorefrontLayout
      apiBaseUrl={apiBaseUrl}
      currency={currency}
      onChangeCurrency={setCurrency}
    >
      <section className="subpage-hero">
        <p className="hero-kicker">{t("contactHeroKicker")}</p>
        <h2>{t("contactTitle")}</h2>
        <p>
          {t("contactIntro")}
        </p>
      </section>

      <section className="contact-layout">
        <article className="contact-form-card">
          <h3>{t("contactFormTitle")}</h3>
          <form className="auth" onSubmit={submit}>
            <div className="seo-inquiry-field">
              <span className="seo-inquiry-field-icon">{renderInquiryFieldIcon("name")}</span>
              <input
                placeholder={t("homeFormNamePlaceholder")}
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                required
              />
            </div>
            <div className="seo-inquiry-field">
              <span className="seo-inquiry-field-icon">{renderInquiryFieldIcon("email")}</span>
              <input
                type="email"
                placeholder={t("homeFormEmailPlaceholder")}
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                required
              />
            </div>
            <div className="seo-inquiry-field">
              <span className="seo-inquiry-field-icon">{renderInquiryFieldIcon("whatsapp")}</span>
              <input
                placeholder={t("homeFormWhatsappPlaceholder")}
                value={contactWhatsapp}
                onChange={(event) => setContactWhatsapp(event.target.value)}
              />
            </div>
            <div className="seo-inquiry-field">
              <span className="seo-inquiry-field-icon">{renderInquiryFieldIcon("company")}</span>
              <input
                placeholder={t("homeFormCompanyPlaceholder")}
                value={contactCompany}
                onChange={(event) => setContactCompany(event.target.value)}
              />
            </div>
            <div className="seo-inquiry-field">
              <span className="seo-inquiry-field-icon">{renderInquiryFieldIcon("product")}</span>
              <input
                placeholder={t("homeFormProductPlaceholder")}
                value={inquiryProduct}
                onChange={(event) => setInquiryProduct(event.target.value)}
                required
              />
            </div>
            <div className="seo-inquiry-field">
              <span className="seo-inquiry-field-icon">{renderInquiryFieldIcon("quantity")}</span>
              <input
                placeholder={t("homeFormQuantityPlaceholder")}
                value={inquiryQuantity}
                onChange={(event) => setInquiryQuantity(event.target.value)}
              />
            </div>
            <div className="seo-inquiry-field seo-inquiry-field-textarea">
              <span className="seo-inquiry-field-icon">{renderInquiryFieldIcon("message")}</span>
              <textarea
                rows={6}
                placeholder={t("homeFormMessagePlaceholder")}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                required
              />
            </div>
            <label className="seo-inquiry-upload" htmlFor="contact-reference-file">
              {t("homeFormUploadLabel")}
            </label>
            <label className="seo-inquiry-upload-control" htmlFor="contact-reference-file">
              <span className="seo-upload-button">{t("homeFormChooseFile")}</span>
              <span className={`seo-upload-filename${referenceFileName ? " has-file" : ""}`}>
                {referenceFileName || t("homeFormNoFile")}
              </span>
            </label>
            <input
              id="contact-reference-file"
              type="file"
              className="seo-upload-input"
              onChange={(event) => {
                const file = event.target.files && event.target.files[0];
                setReferenceFileName(file ? file.name : "");
              }}
            />
            {referenceFileName ? <p className="small muted">{t("homeFormSelectedFilePrefix")} {referenceFileName}</p> : null}
            <div className="auth-actions">
              <button className="button" type="submit" disabled={submitting}>
                {submitting ? t("inquirySubmitting") : t("sendMessage")}
              </button>
            </div>
          </form>
          {inquiryPrefill ? (
            <p className="small muted">
              {t("contactInquiryPresetLoaded")} {inquiryPrefill.productName || t("contactSelectedProduct")}
              {inquiryPrefill.sku ? ` (SKU: ${inquiryPrefill.sku})` : ""}.
            </p>
          ) : null}
          {sent ? <p className="state">{t("contactSent")}</p> : null}
          {submitError ? <p className="state error">{submitError}</p> : null}
        </article>

        <aside className="contact-info-card">
          <h3>{t("contactInfoTitle")}</h3>
          {displayContactEmail ? <p>{`${t("footerEmailLabel")}: ${displayContactEmail}`}</p> : null}
          {displayContactPhone ? <p>{`${t("footerPhoneLabel")}: ${displayContactPhone}`}</p> : null}
          {displayContactWhatsapp ? <p>{`${t("footerWhatsappLabel")}: ${displayContactWhatsapp}`}</p> : null}
          <p>{t("contactInfoHours")}</p>
          <div className="contact-note">
            {t("contactInfoNote")}
          </div>
        </aside>
      </section>

      <section className="info-cards">
        <article>
          <h3>{t("contactResponseTitle")}</h3>
          <p>{t("contactResponseDesc")}</p>
        </article>
        <article>
          <h3>{t("contactDocsTitle")}</h3>
          <p>{t("contactDocsDesc")}</p>
        </article>
        <article>
          <h3>{t("contactSampleTitle")}</h3>
          <p>{t("contactSampleDesc")}</p>
        </article>
      </section>

      <section className="subpage-panel">
        <p>{t("contactQuoteHint")}</p>
      </section>
    </StorefrontLayout>
  );
}
